// Ripped from https://github.com/dsb-norge/vue-keycloak-js/blob/master/src/index.ts

import Keycloak from 'keycloak-js';
import { reactive } from '@vue/reactivity';

import store from '../store';

/* eslint-disable no-param-reassign */
let installed = false;

const vue2AndVue3Reactive = (app, object) => {
  if (app.prototype) {
    // Vue 2
    const reactiveObj = app.observable(object);
    Object.defineProperty(app.prototype, '$keycloak', {
      get () {
        return reactiveObj;
      },

    });

    return reactiveObj;
  }

  // Vue 3
  const reactiveObj = reactive(object);
  app.config.globalProperties.$keycloak = reactiveObj;

  return reactiveObj;
};

const init = (config, watch, options) => {
  const keycloak = Keycloak(config);

  const updateWatchVariables = (isAuthenticated = false) => {
    watch.authenticated = isAuthenticated;
    watch.loginFn = keycloak.login;
    watch.login = keycloak.login;
    watch.createLoginUrl = keycloak.createLoginUrl;
    watch.createLogoutUrl = keycloak.createLogoutUrl;
    watch.createRegisterUrl = keycloak.createRegisterUrl;
    watch.register = keycloak.register;
    watch.keycloak = keycloak;

    if (isAuthenticated) {
      watch.accountManagement = keycloak.accountManagement;
      watch.createAccountUrl = keycloak.createAccountUrl;
      watch.hasRealmRole = keycloak.hasRealmRole;
      watch.hasResourceRole = keycloak.hasResourceRole;
      watch.loadUserProfile = keycloak.loadUserProfile;
      watch.token = keycloak.token;
      watch.subject = keycloak.subject;
      watch.idToken = keycloak.idToken;
      watch.idTokenParsed = keycloak.idTokenParsed;
      watch.realmAccess = keycloak.realmAccess;
      watch.resourceAccess = keycloak.resourceAccess;
      watch.refreshToken = keycloak.refreshToken;
      watch.refreshTokenParsed = keycloak.refreshTokenParsed;
      watch.timeSkew = keycloak.timeSkew;
      watch.responseMode = keycloak.responseMode;
      watch.responseType = keycloak.responseType;
      watch.tokenParsed = keycloak.tokenParsed;
      watch.userName = keycloak.tokenParsed.preferred_username;
      watch.fullName = keycloak.tokenParsed.name;
    }
  };

  keycloak.onReady = function onReady (authenticated) {
    updateWatchVariables(authenticated);
    watch.ready = true;
    typeof options.onReady === 'function' && options.onReady(keycloak, watch);
  };

  keycloak.onAuthSuccess = function onAuthSuccess () {
    // Check token validity every 10 seconds (10 000 ms) and, if necessary, update the token.
    // Refresh token if it's valid for less than 60 seconds
    const updateTokenInterval = setInterval(() => keycloak.updateToken(60).catch(() => {
      keycloak.clearToken();
    }), 10000);

    watch.logoutFn = () => {
      clearInterval(updateTokenInterval);
      keycloak.logout(options.logout);
    };
  };

  keycloak.onAuthRefreshSuccess = function onAuthRefreshSuccess () {
    updateWatchVariables(true);
  };

  keycloak.onAuthRefreshError = function onAuthRefreshError () {
    updateWatchVariables(false);
    typeof options.onAuthRefreshError === 'function' && options.onAuthRefreshError(keycloak);
  };

  keycloak.init(options.init).then(authenticated => {
    updateWatchVariables(authenticated);
    typeof options.onInitSuccess === 'function' && options.onInitSuccess(authenticated);
  }).catch(err => {
    updateWatchVariables(false);
    const error = Error('Could not initialized keycloak-js adapter');
    typeof options.onInitError === 'function'
      ? options.onInitError(error, err)
      : console.error(error, err); // eslint-disable-line no-console
    if (err.error === 'access_denied') {
      store.updateErrorState(err.error);
    }
  });
};

const _isObject = obj => // eslint-disable-line no-underscore-dangle
  obj !== null && typeof obj === 'object' && Object.prototype.toString.call(obj) !== '[object Array]';

const assertOptions = options => {
  const {
    config,
    init, // eslint-disable-line no-shadow
    onReady,
    onInitError,
    onAuthRefreshError,
  } = options;

  if (typeof config !== 'string' && !_isObject(config)) {
    return {
      hasError: true,
      error: `'config' option must be a string or an object. Found: '${config}'`,
    };
  }

  if (!_isObject(init) || typeof init.onLoad !== 'string') {
    return {
      hasError: true,
      error: `'init' option must be an object with an 'onLoad' property. Found: '${init}'`,
    };
  }

  if (onReady && typeof onReady !== 'function') {
    return {
      hasError: true,
      error: `'onReady' option must be a function. Found: '${onReady}'`,
    };
  }

  if (onInitError && typeof onInitError !== 'function') {
    return {
      hasError: true,
      error: `'onInitError' option must be a function. Found: '${onInitError}'`,
    };
  }

  if (onAuthRefreshError && typeof onAuthRefreshError !== 'function') {
    return {
      hasError: true,
      error: `'onAuthRefreshError' option must be a function. Found: '${onAuthRefreshError}'`,
    };
  }

  return {
    hasError: false,
    error: null,
  };
};

const getConfig = config => {
  if (_isObject(config)) return Promise.resolve(config);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', config);
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(Error(xhr.statusText));
        }
      }
    };

    xhr.send();
  });
};

export default {
  install (app, params = {}) {
    if (installed) return;
    installed = true;
    const defaultParams = {
      // eslint-disable-next-line no-underscore-dangle
      config: window.__BASEURL__ ? `${window.__BASEURL__}/config` : '/config',
      init: {
        onLoad: 'login-required',
      },
    };

    const options = { ...defaultParams, ...params };
    if (assertOptions(options).hasError) {
      throw new Error(`Invalid options given: ${assertOptions(options).error}`);
    }

    const watch = vue2AndVue3Reactive(app, {
      ready: false,
      authenticated: false,
      userName: null,
      fullName: null,
      token: null,
      tokenParsed: null,
      logoutFn: null,
      loginFn: null,
      login: null,
      createLoginUrl: null,
      createLogoutUrl: null,
      createRegisterUrl: null,
      register: null,
      accountManagement: null,
      createAccountUrl: null,
      loadUserProfile: null,
      subject: null,
      idToken: null,
      idTokenParsed: null,
      realmAccess: null,
      resourceAccess: null,
      refreshToken: null,
      refreshTokenParsed: null,
      timeSkew: null,
      responseMode: null,
      responseType: null,
      hasRealmRole: null,
      hasResourceRole: null,
      keycloak: null,
    });

    getConfig(options.config).then(config => {
      init(config, watch, options);
    }).catch(err => {
      console.log(err); // eslint-disable-line no-console
    });
  },
};
